<template>
  <div class="gs-app gv-video-settings">
    <loading
      :active.sync="isLoading"
      :is-full-page="fullPageLoader"
      :color="loaderColor"
    ></loading>
    <gs-card class="gv-video-settings__legacy-banner">
      <gs-icon name="settings" size="18" class="gv-video-settings__legacy-icon" />
      <div class="gv-video-settings__legacy-copy">
        <h2>{{ legacyBanner.title }}</h2>
        <p>{{ legacyBanner.body }}</p>
      </div>
    </gs-card>
    <v-wait for="integrations">
      <half-circle-spinner
        slot="waiting"
        style="display: block; margin: 0 auto"
        :animation-duration="1000"
        :size="60"
        color="#3d4852"
      />
      <div
        class="gv-video-settings__surface
          flex flex-col
          w-full
          bg-white
          rounded
          border-grey-300 border border-solid
        "
      >
        <div class="gv-video-settings__tabbar">
          <div
            class="gv-video-settings__segmented"
            role="group"
            aria-label="Settings sections"
          >
            <button
              v-for="tab in storageSettingsTabs"
              :key="tab.value"
              type="button"
              :class="['gv-video-settings__segment', { 'is-active': storageSettingsTab === tab.value }]"
              :aria-pressed="storageSettingsTab === tab.value ? 'true' : 'false'"
              @click="handleStorageTabChange(tab.value)"
            >
              {{ tab.title }}
            </button>
          </div>
        </div>
        <div class="my-4 px-6">
          <div v-if="storageSettingsTab === 'encoding'">
            <encoder-settings
              :encodingAccounts="encodingAccounts"
              @setIsLoading="isLoading = $event"
            >
            </encoder-settings>
          </div>
          <div v-if="storageSettingsTab === 'youtube' && currentUserAllowed">
            <youtube-settings
              :youtubeAccounts="youtubeAccounts"
              @setIsLoading="isLoading = $event"
            >
            </youtube-settings>
          </div>
          <div v-if="storageSettingsTab === 'grooveStorage' || storageSettingsTab === 'ownStorage'">
            <storage-settings
              :storageAccounts="storageAccounts"
              @setIsLoading="isLoading = $event"
              :totalStorage="totalStorage"
              :storageUsed="storageUsed"
              :storageSettingsTab="storageSettingsTab"
            >
            </storage-settings>
          </div>
        </div>
      </div>
    </v-wait>
  </div>
</template>
<script>
import axios from 'axios';
import Loading from 'vue-loading-overlay';
import themeConfig from '@/theme/themeConfig.js';
import 'vue-loading-overlay/dist/vue-loading.css';
import Storage from '@/models/Storage';
import Encoder from '@/models/Encoder';
import YoutubeApiSettings from '@/models/YoutubeApiSettings.js';
import StorageSettings from './storageSettings';
import EncoderSettings from './encoderSettings';
import YoutubeSettings from './youtubeSettings';


export default {
  middleware: 'auth',
  props: {
    section: {
      type: String,
      default: 'storage',
    },
  },
  components: {
    Loading,
    StorageSettings,
    EncoderSettings,
    YoutubeSettings
  },
  data () {
    return {
      isLoading: false,
      fullPageLoader: true,
      loaderColor: themeConfig.appPrimaryColor,
      storageSettingsTab: 'ownStorage',
      totalStorage: 100,
      storageUsed: 0,
      storageAccounts: [],
      encodingAccounts: [],
      youtubeAccounts: [],
    };
  },
  computed: {
    currentUserAllowed() {
      // Check if current user is allowed for testing new features
      const allowedUsers = [
        '5f0d5d4a6ca1bd0016c5142f',
        // '9d280130-6f1d-429a-8231-b7d77de8c3c4',
      ];

      return allowedUsers.includes(this.$store.state.user.id);
    },
    legacyBanner() {
      if (this.section === 'encoder') {
        return {
          title: 'Legacy encoders',
          body: 'Encoding profiles apply only to videos uploaded to Groove storage. New videos use a YouTube link and are encoded by YouTube.',
        };
      }
      return {
        title: 'Legacy storage',
        body: 'These settings apply only to videos uploaded before September 2026. New videos use a YouTube link and need no storage.',
      };
    },
    storageSettingsTabs () {
      if (this.section === 'encoder') {
        const tabs = [
          {
            title: this.$t(
              'frontend_groovevideo_members_settings_index_video_encoder'
            ),
            value: 'encoding',
          },
        ];
        if (this.currentUserAllowed) {
          tabs.push({
            title: this.$t(
              'frontend_groovevideo_members_settings_index_youtube_settings'
            ),
            value: 'youtube',
          });
        }
        return tabs;
      }

      const tabs = [
        {
          title: this.$t(
            'frontend_groovevideo_members_settings_index_groove_storage_settings'
          ),
          value: 'grooveStorage',
        },
        {
          title: this.$t(
            'frontend_groovevideo_members_settings_index_own_storage_settings'
          ),
          value: 'ownStorage',
        },
      ];
      if(this.$store.getters.canUseStorage === 0) {
        tabs.shift();
      }
      return tabs;
    },
  },
  watch: {
    '$route.hash'(hash) {
      if (this.section === 'encoder' && hash === '#youtube' && this.currentUserAllowed) {
        this.storageSettingsTab = 'youtube';
      }
    },
  },
  mounted () {
    // import google api
    if (document.getElementById('googleApis')) return; // was already loaded
    const scriptTag = document.createElement("script");
    scriptTag.src = "https://accounts.google.com/gsi/client";
    scriptTag.setAttribute("onload", "console.log('loaded')");
    scriptTag.id = "googleApis";
    document.getElementsByTagName('head')[0].appendChild(scriptTag);
  },
  async created() {
    this.$wait.start('integrations');
    this.storageAccounts = await Storage.$get();
    this.encodingAccounts = await Encoder.$get();
    this.youtubeAccounts = await YoutubeApiSettings.$get();
    // `#ownStorage` / `#grooveStorage` still arrive from the legacy upload
    // pages, which use the hash to pick a tab, not just a section.
    const hashedTab = (this.$route.hash || '').replace('#', '');
    const hashedTabExists = this.storageSettingsTabs.some(
      tab => tab.value === hashedTab
    );
    this.storageSettingsTab = this.section === 'encoder'
      ? (this.$route.hash === '#youtube' && this.currentUserAllowed ? 'youtube' : 'encoding')
      : (hashedTabExists ? hashedTab : this.storageSettingsTabs[0].value);

    if (this.section === 'storage' && this.$store.getters.canUseStorage !== 0) {
      const remainingData = await axios.get('remaining');
      let remaining = 0;
      
      if (remainingData && remainingData.data && remainingData.data.data) {
        // Convert Bytes to GB
        remaining = (remainingData.data.data.res / 1000000000);
      }
      this.totalStorage = Math.floor((this.$store.getters.maxStorage / 1000000000));

      // Calculate storage used to 2nd place of demical.
      this.storageUsed = (this.totalStorage - remaining).toFixed(2);
    }
    this.$wait.end('integrations');
  },
  methods: {
    handleStorageTabChange(newTab) {
      this.storageSettingsTab = newTab;
    },
  },
};
</script>
<style>
.settingstabHead {
  width: 100%;
  display: inline-block;
  border-bottom: 1px solid #f7f7fa;
}
.settingstabHead .tabs__item {
  padding: 15px 20px;
}
.settingstabHead .tabs__item:active,
.settingstabHead .tabs__item:focus {
  outline: none;
}
</style>

<style>
.gv-video-settings__surface { gap: 12px; overflow: visible; border-color: var(--gs-border); border-radius: var(--gs-radius-lg); background: var(--gs-surface); box-shadow: var(--gs-shadow-sm); }
.gv-video-settings__surface > .my-4 { padding: 20px; }
@media (max-width: 640px) { .gv-video-settings__surface > .my-4 { padding: 16px; } }
.gv-video-settings__legacy-banner { margin-bottom: 16px; }
.gv-video-settings__legacy-banner h2 { margin: 0 0 4px; color: var(--gs-foreground); }
.gv-video-settings__legacy-banner p { margin: 0; color: var(--gs-muted-foreground); }
</style>

<style>
.gv-video-settings__legacy-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 20px;
  border-left: 3px solid var(--gs-warning);
}

.gv-video-settings__legacy-icon {
  flex: none;
  margin-top: 2px;
  color: var(--gs-warning);
}

.gv-video-settings__legacy-banner h2 {
  font-size: 15px;
  font-weight: 700;
}

.gv-video-settings__legacy-banner p {
  font-size: 14px;
  max-width: 52rem;
}

.gv-video-settings__tabbar {
  display: flex;
  padding: 16px 20px 0;
}

.gv-video-settings__segmented {
  display: inline-flex;
  padding: 3px;
  background: var(--gs-muted);
  border-radius: var(--gs-radius-md);
}

.gv-video-settings__segment {
  padding: 6px 14px;
  color: var(--gs-muted-foreground);
  background: transparent;
  border: 0;
  border-radius: var(--gs-radius-sm);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.gv-video-settings__segment:hover {
  color: var(--gs-foreground);
}

.gv-video-settings__segment:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gv-video-settings__segment.is-active {
  color: var(--gs-foreground);
  background: var(--gs-surface);
  box-shadow: var(--gs-shadow-sm);
}

@media (max-width: 640px) {
  .gv-video-settings__tabbar {
    padding: 16px 16px 0;
  }

  .gv-video-settings__segmented {
    width: 100%;
  }

  .gv-video-settings__segment {
    flex: 1 1 0;
  }
}
</style>
